import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  Pencil,
  Copy,
  Trash2,
  ExternalLink,
  Link2,
  BarChart3,
  MoreHorizontal,
  Play,
  Pause,
  Archive,
  MapPin,
  Building2,
  Users,
  Handshake,
  Home,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

export default function URLDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    destination_url: '',
    tracking: {
      region_id: '',
      region_name: '',
      office_id: '',
      office_name: '',
      agent_id: '',
      agent_name: '',
      partnership_id: '',
      partnership_name: '',
      listing_ref: '',
    },
    utm: {
      utm_campaign: '',
    },
  });
  const [referenceData, setReferenceData] = useState({
    regions: [],
    offices: [],
    agents: [],
    partnerships: [],
  });

  useEffect(() => {
    fetchUrl();
    fetchReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-open the edit dialog when ?edit=1 is present (e.g. from the list's
  // Edit action). Waits for the URL to load so the form is pre-populated,
  // then strips the flag so closing the dialog doesn't leave it primed.
  useEffect(() => {
    if (!url) return;
    if (searchParams.get('edit') === '1') {
      setEditDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [url, searchParams, setSearchParams]);

  const fetchReferenceData = async () => {
    try {
      const response = await apiV1.get('/reference-data/options');
      if (response.data.success) {
        setReferenceData({
          regions: response.data.data.region || [],
          offices: response.data.data.office || [],
          agents: response.data.data.agent || [],
          partnerships: response.data.data.partnership || [],
        });
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const fetchUrl = async () => {
    setLoading(true);
    try {
      const response = await apiV1.get(`/urls/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        setUrl(data);
        setEditForm({
          title: data.title || '',
          description: data.description || '',
          destination_url: data.destination_url || '',
          tracking: {
            region_id: data.tracking?.region_id || '',
            region_name: data.tracking?.region_name || '',
            office_id: data.tracking?.office_id || '',
            office_name: data.tracking?.office_name || '',
            agent_id: data.tracking?.agent_id || '',
            agent_name: data.tracking?.agent_name || '',
            partnership_id: data.tracking?.partnership_id || '',
            partnership_name: data.tracking?.partnership_name || '',
            listing_ref: data.tracking?.listing_ref || '',
          },
          utm: {
            utm_campaign: data.utm?.utm_campaign || '',
          },
        });
      }
    } catch (error) {
      console.error('Error fetching short URL:', error);
      toast.error('Failed to load short URL');
      navigate('/urls');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await apiV1.put(`/urls/${id}`, { status: newStatus });
      toast.success(
        `Short URL ${newStatus === 'active' ? 'activated' : newStatus === 'paused' ? 'paused' : 'updated'}`
      );
      fetchUrl();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to archive this short URL?')) return;
    try {
      await apiV1.delete(`/urls/${id}`);
      toast.success('Short URL archived');
      navigate('/urls');
    } catch (error) {
      toast.error('Failed to delete short URL');
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description || null,
        destination_url: editForm.destination_url,
        tracking: {
          region_id: editForm.tracking.region_id || null,
          region_name: editForm.tracking.region_name || null,
          office_id: editForm.tracking.office_id || null,
          office_name: editForm.tracking.office_name || null,
          agent_id: editForm.tracking.agent_id || null,
          agent_name: editForm.tracking.agent_name || null,
          partnership_id: editForm.tracking.partnership_id || null,
          partnership_name: editForm.tracking.partnership_name || null,
          listing_ref: editForm.tracking.listing_ref || null,
        },
        utm: {
          utm_source: url.utm?.utm_source || 'shortlink',
          utm_medium: url.utm?.utm_medium || 'web',
          utm_campaign: editForm.utm.utm_campaign || null,
        },
      };
      await apiV1.put(`/urls/${id}`, payload);
      toast.success('Short URL updated');
      setEditDialogOpen(false);
      fetchUrl();
    } catch (error) {
      console.error('Failed to update short URL:', error);
      const message =
        error?.message ||
        error?.response?.data?.detail?.message ||
        'Failed to update short URL';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updateTrackingField = (field, value, options, nameField) => {
    const selected = options?.find((o) => o.id === value);
    setEditForm((prev) => ({
      ...prev,
      tracking: {
        ...prev.tracking,
        [field]: value,
        [nameField]: selected?.name || '',
      },
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { variant: 'default', label: 'Active' },
      paused: { variant: 'secondary', label: 'Paused' },
      archived: { variant: 'outline', label: 'Archived' },
      expired: { variant: 'destructive', label: 'Expired' },
    };
    const { variant, label } = config[status] || { variant: 'outline', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-96 col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="text-center py-12">
        <Link2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium">Short URL not found</h2>
        <p className="text-muted-foreground mb-4">The short URL you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/urls">Back to Short URLs</Link>
        </Button>
      </div>
    );
  }

  const shortUrl = url.short_url || `/${url.short_code}`;

  return (
    <div className="space-y-6" data-testid="url-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/urls">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{url.title}</h1>
              {getStatusBadge(url.status)}
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Slug: <code className="bg-muted px-1 rounded">{url.short_code}</code>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="url-edit-button">
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>

          <Button variant="outline" asChild>
            <Link to={`/urls/analytics/${id}`}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Link>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {url.status === 'active' && (
                <DropdownMenuItem onClick={() => handleStatusChange('paused')}>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </DropdownMenuItem>
              )}
              {url.status === 'paused' && (
                <DropdownMenuItem onClick={() => handleStatusChange('active')}>
                  <Play className="h-4 w-4 mr-2" />
                  Activate
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleStatusChange('archived')}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="details" className="w-full">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="tracking">Tracking</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Destination</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Target URL</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-sm bg-muted p-2 rounded truncate">
                        {url.destination_url}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(url.destination_url, 'URL')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" asChild>
                        <a href={url.destination_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>

                  {url.tracked_url && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Tracked URL (with UTM)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 text-xs bg-muted p-2 rounded break-all">
                          {url.tracked_url}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyToClipboard(url.tracked_url, 'Tracked URL')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Created</Label>
                      <p className="text-sm font-medium mt-1">
                        {new Date(url.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Last Updated</Label>
                      <p className="text-sm font-medium mt-1">
                        {new Date(url.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    {url.expires_at && (
                      <div>
                        <Label className="text-sm text-muted-foreground">Expires</Label>
                        <p className="text-sm font-medium mt-1">
                          {new Date(url.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {url.password_hash && (
                      <div>
                        <Label className="text-sm text-muted-foreground">Password protected</Label>
                        <p className="text-sm font-medium mt-1">Yes</p>
                      </div>
                    )}
                  </div>

                  {url.tags && url.tags.length > 0 && (
                    <div className="mt-4">
                      <Label className="text-sm text-muted-foreground">Tags</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {url.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {url.description && (
                    <div className="mt-4">
                      <Label className="text-sm text-muted-foreground">Description</Label>
                      <p className="text-sm mt-1">{url.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="analytics" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Click Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold">{url.total_clicks || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Clicks</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold">{url.unique_clicks || 0}</p>
                      <p className="text-sm text-muted-foreground">Unique Clicks</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold">
                        {url.last_click_at
                          ? new Date(url.last_click_at).toLocaleDateString()
                          : 'Never'}
                      </p>
                      <p className="text-sm text-muted-foreground">Last Click</p>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <Button variant="outline" asChild>
                      <Link to={`/urls/analytics/${id}`}>
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Full analytics
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tracking" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tracking Parameters</CardTitle>
                  <CardDescription>Attribution data for analytics filtering</CardDescription>
                </CardHeader>
                <CardContent>
                  {url.tracking && Object.values(url.tracking).some((v) => v) ? (
                    <div className="grid grid-cols-2 gap-4">
                      {url.tracking.region_name && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Region</p>
                            <p className="text-sm font-medium">{url.tracking.region_name}</p>
                          </div>
                        </div>
                      )}
                      {url.tracking.office_name && (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Office</p>
                            <p className="text-sm font-medium">{url.tracking.office_name}</p>
                          </div>
                        </div>
                      )}
                      {url.tracking.agent_name && (
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Agent</p>
                            <p className="text-sm font-medium">{url.tracking.agent_name}</p>
                          </div>
                        </div>
                      )}
                      {url.tracking.partnership_name && (
                        <div className="flex items-center gap-2">
                          <Handshake className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Partnership</p>
                            <p className="text-sm font-medium">{url.tracking.partnership_name}</p>
                          </div>
                        </div>
                      )}
                      {url.tracking.listing_ref && (
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Listing Ref</p>
                            <p className="text-sm font-medium">{url.tracking.listing_ref}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No tracking parameters set</p>
                  )}

                  {url.utm && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium mb-2">UTM Parameters</p>
                      <div className="flex flex-wrap gap-2">
                        {url.utm.utm_source && (
                          <Badge variant="outline">source={url.utm.utm_source}</Badge>
                        )}
                        {url.utm.utm_medium && (
                          <Badge variant="outline">medium={url.utm.utm_medium}</Badge>
                        )}
                        {url.utm.utm_campaign && (
                          <Badge variant="outline">campaign={url.utm.utm_campaign}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Short URL</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-muted p-2 rounded break-all">{shortUrl}</code>
                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(shortUrl, 'Short URL')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full justify-start" variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button className="w-full justify-start" variant="outline" asChild>
                <Link to={`/urls/analytics/${id}`}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View analytics
                </Link>
              </Button>
              <Button className="w-full justify-start" variant="outline" onClick={() => copyToClipboard(shortUrl, 'Short URL')}>
                <Copy className="h-4 w-4 mr-2" />
                Copy short URL
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Short URL</DialogTitle>
            <DialogDescription>
              Update the details, destination, and tracking parameters for this short URL.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Basic Information
                </h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title *</Label>
                  <Input
                    id="edit-title"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    data-testid="url-edit-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={2}
                    data-testid="url-edit-description-input"
                  />
                </div>
              </div>

              <Separator />

              {/* Destination URL */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Destination
                </h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-url">Destination URL *</Label>
                  <Input
                    id="edit-url"
                    type="url"
                    value={editForm.destination_url}
                    onChange={(e) => setEditForm({ ...editForm, destination_url: e.target.value })}
                    placeholder="https://example.com"
                    data-testid="url-edit-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Visitors are redirected here. Must start with http:// or https://.
                  </p>
                </div>
              </div>

              <Separator />

              {/* Tracking Parameters */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Tracking Parameters
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      Region
                    </Label>
                    <Select
                      value={editForm.tracking.region_id || 'none'}
                      onValueChange={(value) =>
                        updateTrackingField('region_id', value === 'none' ? '' : value, referenceData.regions, 'region_name')
                      }
                    >
                      <SelectTrigger data-testid="url-edit-region-select">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.regions.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      Office
                    </Label>
                    <Select
                      value={editForm.tracking.office_id || 'none'}
                      onValueChange={(value) =>
                        updateTrackingField('office_id', value === 'none' ? '' : value, referenceData.offices, 'office_name')
                      }
                    >
                      <SelectTrigger data-testid="url-edit-office-select">
                        <SelectValue placeholder="Select office" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.offices.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      Agent
                    </Label>
                    <Select
                      value={editForm.tracking.agent_id || 'none'}
                      onValueChange={(value) =>
                        updateTrackingField('agent_id', value === 'none' ? '' : value, referenceData.agents, 'agent_name')
                      }
                    >
                      <SelectTrigger data-testid="url-edit-agent-select">
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.agents.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Handshake className="h-4 w-4 text-muted-foreground" />
                      Partnership
                    </Label>
                    <Select
                      value={editForm.tracking.partnership_id || 'none'}
                      onValueChange={(value) =>
                        updateTrackingField('partnership_id', value === 'none' ? '' : value, referenceData.partnerships, 'partnership_name')
                      }
                    >
                      <SelectTrigger data-testid="url-edit-partnership-select">
                        <SelectValue placeholder="Select partnership" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.partnerships.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-muted-foreground" />
                    Listing Reference
                  </Label>
                  <Input
                    value={editForm.tracking.listing_ref}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        tracking: { ...prev.tracking, listing_ref: e.target.value },
                      }))
                    }
                    placeholder="e.g., PROP-2024-001"
                    data-testid="url-edit-listing-ref-input"
                  />
                </div>
              </div>

              <Separator />

              {/* UTM Parameters */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  UTM Parameters
                </h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-campaign">Campaign Name</Label>
                  <Input
                    id="edit-campaign"
                    value={editForm.utm.utm_campaign}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        utm: {
                          ...prev.utm,
                          utm_campaign: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '-'),
                        },
                      }))
                    }
                    placeholder="e.g., summer-2024"
                    data-testid="url-edit-campaign-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    utm_source and utm_medium are set automatically.
                  </p>
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving || !editForm.title.trim() || !editForm.destination_url.trim()}
              data-testid="url-edit-save-button"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
